/**
 * Live-progress plumbing for streaming executors (BE / substrate).
 *
 * Two small, independently-testable pieces:
 *
 *  - `StreamJsonTap` — fed raw `--output-format stream-json` stdout chunks, it
 *    parses complete JSON lines as they arrive and surfaces the live signal a
 *    progress view needs: assistant `text`/`thinking` deltas as reasoning, and
 *    `tool_use` blocks as activity. Deliberately separate from the authoritative
 *    full-buffer `parseStreamJson` (which extracts the terminal result); this one
 *    is side-effect-only and best-effort.
 *
 *  - `ProgressReporter` — coalesces that signal into batched `ProgressFrame`s and
 *    flushes them through a sink. Reasoning text is buffered and flushed on a
 *    short timer or a size threshold (so we POST tens of batches, not thousands
 *    of token deltas); tool activity flushes immediately, draining any pending
 *    text first so the transcript stays ordered. Monotonic `seq` lets a
 *    reconnecting consumer order/dedupe. Every sink call is guarded — a progress
 *    failure must never affect the job.
 */

import type { ProgressFrame } from '@whittlelabs/jobs';

export interface ProgressReporterOptions {
  /** Max ms a reasoning chunk waits before flushing. Default 400. */
  flushMs?: number;
  /** Flush accumulated reasoning once it reaches this many chars. Default 1200. */
  maxChars?: number;
}

export class ProgressReporter {
  private seq = 0;
  private pending: ProgressFrame[] = [];
  private textBuf = '';
  private timer: ReturnType<typeof setTimeout> | null = null;
  private closed = false;
  private readonly flushMs: number;
  private readonly maxChars: number;

  constructor(
    private readonly sink: (frames: ProgressFrame[]) => void,
    options: ProgressReporterOptions = {},
  ) {
    this.flushMs = options.flushMs ?? 400;
    this.maxChars = options.maxChars ?? 1200;
  }

  reasoning(text: string): void {
    if (this.closed || !text) return;
    this.textBuf += text;
    if (this.textBuf.length >= this.maxChars) {
      this.flush();
    } else {
      this.arm();
    }
  }

  tool(name: string, target?: string): void {
    if (this.closed || !name) return;
    this.drainText();
    this.push({ kind: 'tool', tool: target ? { name, target } : { name } });
    this.flush();
  }

  /** Flush pending text + frames now. */
  flush(): void {
    if (this.closed) return;
    this.drainText();
    this.clearTimer();
    if (this.pending.length === 0) return;
    const batch = this.pending;
    this.pending = [];
    try {
      this.sink(batch);
    } catch {
      // best-effort; a progress sink failure must never affect the job.
    }
  }

  /** Final flush; no further frames are emitted after this. */
  close(): void {
    this.flush();
    this.closed = true;
    this.clearTimer();
  }

  private drainText(): void {
    if (this.textBuf.length === 0) return;
    this.push({ kind: 'reasoning', text: this.textBuf });
    this.textBuf = '';
  }

  private push(frame: Omit<ProgressFrame, 'seq' | 'at'>): void {
    this.pending.push({ seq: this.seq++, at: new Date().toISOString(), ...frame });
  }

  private arm(): void {
    if (this.timer) return;
    this.timer = setTimeout(() => {
      this.timer = null;
      this.flush();
    }, this.flushMs);
    // A pending progress flush should never keep the process alive on its own.
    (this.timer as { unref?: () => void }).unref?.();
  }

  private clearTimer(): void {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }
}

export interface StreamJsonTapHandlers {
  onReasoning?(text: string): void;
  onTool?(name: string, target?: string): void;
  onResult?(): void;
}

export class StreamJsonTap {
  private buf = '';

  constructor(private readonly handlers: StreamJsonTapHandlers) {}

  /** Feed a raw stdout chunk. Emits handlers for every complete line parsed. */
  push(chunk: string): void {
    this.buf += chunk;
    let nl: number;
    while ((nl = this.buf.indexOf('\n')) !== -1) {
      const line = this.buf.slice(0, nl);
      this.buf = this.buf.slice(nl + 1);
      this.consumeLine(line);
    }
  }

  /** Flush a trailing partial line (best-effort; usually empty at process close). */
  end(): void {
    if (this.buf.trim().length > 0) this.consumeLine(this.buf);
    this.buf = '';
  }

  private consumeLine(line: string): void {
    const trimmed = line.trim();
    if (!trimmed.startsWith('{')) return;
    let event: Record<string, unknown>;
    try {
      event = JSON.parse(trimmed) as Record<string, unknown>;
    } catch {
      return;
    }
    if (event.type === 'assistant') {
      const content = (event.message as { content?: unknown } | undefined)?.content;
      if (!Array.isArray(content)) return;
      for (const block of content) {
        const b = block as {
          type?: string;
          text?: unknown;
          thinking?: unknown;
          name?: unknown;
          input?: Record<string, unknown>;
        };
        if (b?.type === 'text' && typeof b.text === 'string' && b.text.length > 0) {
          this.handlers.onReasoning?.(b.text);
        } else if (b?.type === 'thinking' && typeof b.thinking === 'string' && b.thinking.length > 0) {
          this.handlers.onReasoning?.(b.thinking);
        } else if (b?.type === 'tool_use' && typeof b.name === 'string') {
          this.handlers.onTool?.(b.name, toolTarget(b.input));
        }
      }
    } else if (event.type === 'result') {
      this.handlers.onResult?.();
    }
  }
}

/** Best-effort human target for a tool call: the file/pattern/command it acts on. */
function toolTarget(input: Record<string, unknown> | undefined): string | undefined {
  if (!input) return undefined;
  for (const key of ['file_path', 'path', 'pattern', 'command', 'url', 'query', 'notebook_path']) {
    const v = input[key];
    if (typeof v === 'string' && v.length > 0) return v;
  }
  return undefined;
}
